<template>
  <v-container class="login-container" fluid >
    <v-row class="fill-height" align="center" justify="center" style="width: 100%;">
      <v-col cols="12" sm="10" md="7" lg="5" xl="4">
        <v-card class="elevated-card" rounded="xl">
          <v-card-text class="pa-6">
            <div class="text-center mb-6">
              <v-icon size="56" color="primary" class="mb-3">mdi-shield-key</v-icon>
              <div class="headline font-weight-bold">{{ $t('login.title') }}</div>
              <div class="subtitle-1 text-medium-emphasis">{{ $t('login.subtitle') }}</div>
            </div>

            <v-form @submit.prevent="onSubmit" :disabled="loading" ref="formRef">
              <v-text-field
                v-model="username"
                :label="$t('login.username')"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-account"
                autocomplete="username"
                :rules="usernameRules"
                class="mb-4"
                hide-details="auto"
                block
              />

              <v-text-field
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                :label="$t('login.password')"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-lock"
                :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append-inner="showPassword = !showPassword"
                autocomplete="current-password"
                :rules="passwordRules"
                class="mb-4"
                hide-details="auto"
                block
              />

              <v-alert
                v-if="errorKey || errorMessage"
                type="error"
                variant="tonal"
                class="mb-4"
                density="comfortable"
                border="start"
              >
                {{ translatedError }}
              </v-alert>

              <v-btn
                type="submit"
                color="primary"
                size="large"
                block
                class="mb-4"
                :loading="loading"
                elevation="3"
              >
                {{ $t('login.signIn') }}
              </v-btn>

              <div class="text-center text-caption text-medium-emphasis">{{ $t('login.httpsNote') }}</div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "Login",
  data() {
    return {
      username: "",
      password: "",
      loading: false,
      showPassword: false,
      errorMessage: "", // legacy; will be replaced by errorKey when present
      errorKey: "",
    };
  },
  computed: {
    translatedError() {
      if (this.errorKey) return this.$t(this.errorKey);
      return this.errorMessage || "";
    },
    // Make rules reactive to locale by depending on $i18n.locale
    usernameRules() {
      const _ = this.$i18n.locale; // reactive dependency
      return [(v) => !!v || this.$t('login.usernameRequired')];
    },
    passwordRules() {
      const _ = this.$i18n.locale; // reactive dependency
      return [(v) => !!v || this.$t('login.passwordRequired')];
    },
  },
  watch: {
    "$i18n.locale"() {
      // Re-run validation to refresh displayed messages when language changes
      if (this.$refs.formRef && this.$refs.formRef.validate) {
        this.$refs.formRef.validate();
      }
    },
  },
  methods: {
    ...mapActions(["login"]),
    async onSubmit() {
      this.errorMessage = "";
      this.errorKey = "";
      if (!this.username || !this.password) return;
      this.loading = true;
      try {
        await this.login({ username: this.username, password: this.password });
        this.$router.replace(this.$route.query.redirect || { name: "Alerts" });
      } catch (e) {
        const key = e?.code || e?.message || 'login.failed';
        this.errorKey = key;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f0f4ff 0%, #ffffff 100%);
  display: grid;
  place-items: center;
  padding: 16px;
}
.elevated-card {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(6px);
  max-width: 600px;
  width: 100%;
  margin-inline: auto;
}
.headline {
  font-size: 1.5rem;
}
@media (max-width: 600px) {
  .elevated-card {
    padding: 24px 16px;
  }
  .headline {
    font-size: 1.3rem;
  }
}
</style>